/**
 * The way back to a deep link (`MCK-20260521-009`, the root `/tasks` landing).
 *
 * A signed-out visit to a protected address is sent to `/login?next=<that address>`, and the
 * sign-in that follows returns there instead of to `/`. That is what makes a bookmarked task, an
 * invitation acceptance or — when R7 brings task and review emails back — a link in a message
 * land on the thing it names rather than on a list the person then has to search.
 *
 * **`next` is attacker-controlled, and this file is the whole defence.** Anybody can mail a link
 * to `/login?next=https://evil.example`, and a sign-in screen that honoured it would hand the
 * reader, freshly authenticated and trusting the address bar, to a look-alike page. So the value
 * is only ever an **in-app path**: one leading `/`, and nothing a browser would read as a host.
 * Everything else is `/`, which the landing rule (`HomeRoute`) then resolves as it always did.
 *
 * THREE DOORS, TWO CARRIERS
 *
 *   - **Password** signs in on `/login` itself, so it reads `next` from the route's search.
 *   - **The sign-in link and SSO** leave the tab — to a mailbox, to an identity provider — and
 *     come back on a URL the backend or the provider built, which carries no `next`. The login
 *     page writes it to `sessionStorage` as the person is sent off, and the landing page reads
 *     it back once. `sessionStorage` is per tab: a link opened in another tab or another browser
 *     finds nothing and falls back to `/` and the landing rule. That is the honest limit of the
 *     mechanism rather than a bug — carrying `next` through the email would mean the backend
 *     writing a client-chosen path into a message, which is a second open-redirect surface to
 *     buy a convenience for the minority who switch tabs.
 */

/** Where the link doors keep `next` while the person is away. Per tab, by construction. */
const STORAGE_KEY = 'kalqa.signIn.next';

/**
 * `value` if it is a path inside this application, `/` otherwise. Pure.
 *
 * Refused, and each for a reason a browser supplies:
 *
 *   - anything not starting with `/` — `https://…`, `javascript:…`, a bare `evil.example`;
 *   - `//host` — protocol-relative, which is a different origin;
 *   - `/\host` and any backslash — WHATWG URL parsing treats `\` as `/` in special schemes, so
 *     `/\evil.example` is `//evil.example` by the time it is followed;
 *   - any control character — the URL parser strips tab and newline, so `/\t/evil.example`
 *     becomes `//evil.example` after this check would have passed it.
 */
export function safeNext(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '/';
  if (!value.startsWith('/')) return '/';
  if (value.startsWith('//')) return '/';
  // The entrance is never somewhere to return *to*: a `next` naming it would send a signed-in
  // person back to the sign-in screen, and a guard that ever produced one would be describing
  // its own redirect as the destination (see `RequireSession`).
  if (value === '/login' || value.startsWith('/login?') || value.startsWith('/auth/')) return '/';
  if (value.includes('\\')) return '/';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return '/';
  }
  return value;
}

/** `true` when `value` would survive `safeNext` unchanged and is somewhere other than `/`. */
export function isReturnPath(value: unknown): value is string {
  return typeof value === 'string' && value !== '/' && safeNext(value) === value;
}

/**
 * Remember `next` for a door that leaves the tab — or forget a stale one.
 *
 * Called with `undefined` too, deliberately: a person who reached `/login` from a deep link an
 * hour ago, gave up, and now signs in from a bare `/login` must not be thrown back to that old
 * address by a value nobody asked for this time.
 */
export function rememberNext(next: string | undefined): void {
  try {
    if (isReturnPath(next)) window.sessionStorage.setItem(STORAGE_KEY, next);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage refused (a privacy mode, a sandboxed frame): the door still works, it just lands
    // on `/` — which is the documented fallback anyway.
  }
}

/** Read `next` once and clear it, already validated. `/` when there is nothing to return to. */
export function takeNext(): string {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return safeNext(stored);
  } catch {
    return '/';
  }
}
